import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Mail, MessageSquare, Send, MapPin, Clock, Instagram, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: ''
  });

  const lang = i18n.language;

  const content = {
    en: {
      title: "Contact Us",
      subtitle: "We'd love to hear from you",
      form: {
        name: "Your Name",
        email: "Email Address",
        subject: "Subject",
        subjects: {
          general: "General Inquiry",
          support: "Technical Support",
          billing: "Billing Question",
          refund: "Refund Request",
          business: "Business Inquiry"
        },
        message: "Your Message",
        messagePlaceholder: "How can we help you?",
        send: "Send Message",
        sending: "Sending..."
      },
      success: "Message sent! We'll respond within 24-48 hours.",
      info: {
        email: "Email",
        emailValue: "support@ellagold.com",
        response: "Response Time",
        responseValue: "24-48 hours",
        social: "Social Media",
        location: "Location",
        locationValue: "Netherlands"
      },
      faq: {
        title: "Frequently Asked Questions",
        items: [
          { q: "How do I access purchased content?", a: "All purchased content is available in your Dashboard under 'My Content'." },
          { q: "Can I get a refund?", a: "Unused token packages can be refunded within 14 days. See our Terms for details." },
          { q: "How does the token system work?", a: "Buy tokens, spend them on exclusive content. Tokens never expire." },
          { q: "What payment methods do you accept?", a: "We accept iDEAL, credit cards, and other methods via Stripe." }
        ]
      }
    },
    nl: {
      title: "Contact",
      subtitle: "We horen graag van je",
      form: {
        name: "Je Naam",
        email: "E-mailadres",
        subject: "Onderwerp",
        subjects: {
          general: "Algemene Vraag",
          support: "Technische Ondersteuning",
          billing: "Factuurvraag",
          refund: "Terugbetaling Aanvragen",
          business: "Zakelijke Vraag"
        },
        message: "Je Bericht",
        messagePlaceholder: "Hoe kunnen we je helpen?",
        send: "Verstuur Bericht",
        sending: "Versturen..."
      },
      success: "Bericht verzonden! We reageren binnen 24-48 uur.",
      info: {
        email: "E-mail",
        emailValue: "support@ellagold.com",
        response: "Reactietijd",
        responseValue: "24-48 uur",
        social: "Social Media",
        location: "Locatie",
        locationValue: "Nederland"
      },
      faq: {
        title: "Veelgestelde Vragen",
        items: [
          { q: "Hoe krijg ik toegang tot gekochte content?", a: "Alle gekochte content is beschikbaar in je Dashboard onder 'Mijn Content'." },
          { q: "Kan ik een terugbetaling krijgen?", a: "Ongebruikte tokenpakketten kunnen binnen 14 dagen worden terugbetaald. Zie onze Voorwaarden." },
          { q: "Hoe werkt het tokensysteem?", a: "Koop tokens, besteed ze aan exclusieve content. Tokens verlopen nooit." },
          { q: "Welke betaalmethoden accepteren jullie?", a: "We accepteren iDEAL, creditcards en andere methoden via Stripe." }
        ]
      }
    },
    de: {
      title: "Kontakt",
      subtitle: "Wir freuen uns von Ihnen zu hören",
      form: {
        name: "Ihr Name",
        email: "E-Mail-Adresse",
        subject: "Betreff",
        subjects: {
          general: "Allgemeine Anfrage",
          support: "Technischer Support",
          billing: "Rechnungsfrage",
          refund: "Rückerstattungsanfrage",
          business: "Geschäftsanfrage"
        },
        message: "Ihre Nachricht",
        messagePlaceholder: "Wie können wir Ihnen helfen?",
        send: "Nachricht senden",
        sending: "Wird gesendet..."
      },
      success: "Nachricht gesendet! Wir antworten innerhalb von 24-48 Stunden.",
      info: { email: "E-Mail", emailValue: "support@ellagold.com", response: "Antwortzeit", responseValue: "24-48 Stunden", social: "Social Media", location: "Standort", locationValue: "Niederlande" },
      faq: { title: "Häufig gestellte Fragen", items: [] }
    },
    fr: {
      title: "Contact",
      subtitle: "Nous serions ravis de vous entendre",
      form: { name: "Votre Nom", email: "Adresse Email", subject: "Sujet", subjects: { general: "Demande Générale", support: "Support Technique", billing: "Question de Facturation", refund: "Demande de Remboursement", business: "Demande Commerciale" }, message: "Votre Message", messagePlaceholder: "Comment pouvons-nous vous aider?", send: "Envoyer", sending: "Envoi..." },
      success: "Message envoyé! Nous répondrons dans les 24-48 heures.",
      info: { email: "Email", emailValue: "support@ellagold.com", response: "Délai de réponse", responseValue: "24-48 heures", social: "Réseaux Sociaux", location: "Emplacement", locationValue: "Pays-Bas" },
      faq: { title: "Questions Fréquentes", items: [] }
    },
    es: {
      title: "Contacto",
      subtitle: "Nos encantaría saber de ti",
      form: { name: "Tu Nombre", email: "Correo Electrónico", subject: "Asunto", subjects: { general: "Consulta General", support: "Soporte Técnico", billing: "Pregunta de Facturación", refund: "Solicitud de Reembolso", business: "Consulta Comercial" }, message: "Tu Mensaje", messagePlaceholder: "¿Cómo podemos ayudarte?", send: "Enviar", sending: "Enviando..." },
      success: "¡Mensaje enviado! Responderemos en 24-48 horas.",
      info: { email: "Email", emailValue: "support@ellagold.com", response: "Tiempo de respuesta", responseValue: "24-48 horas", social: "Redes Sociales", location: "Ubicación", locationValue: "Países Bajos" },
      faq: { title: "Preguntas Frecuentes", items: [] }
    },
    it: {
      title: "Contatti",
      subtitle: "Ci piacerebbe sentirti",
      form: { name: "Il Tuo Nome", email: "Indirizzo Email", subject: "Oggetto", subjects: { general: "Richiesta Generale", support: "Supporto Tecnico", billing: "Domanda sulla Fatturazione", refund: "Richiesta di Rimborso", business: "Richiesta Commerciale" }, message: "Il Tuo Messaggio", messagePlaceholder: "Come possiamo aiutarti?", send: "Invia", sending: "Invio..." },
      success: "Messaggio inviato! Risponderemo entro 24-48 ore.",
      info: { email: "Email", emailValue: "support@ellagold.com", response: "Tempo di risposta", responseValue: "24-48 ore", social: "Social Media", location: "Posizione", locationValue: "Paesi Bassi" },
      faq: { title: "Domande Frequenti", items: [] }
    },
    ru: {
      title: "Контакты",
      subtitle: "Мы будем рады услышать вас",
      form: { name: "Ваше Имя", email: "Email", subject: "Тема", subjects: { general: "Общий вопрос", support: "Техническая поддержка", billing: "Вопрос по оплате", refund: "Запрос на возврат", business: "Деловой запрос" }, message: "Ваше Сообщение", messagePlaceholder: "Как мы можем вам помочь?", send: "Отправить", sending: "Отправка..." },
      success: "Сообщение отправлено! Мы ответим в течение 24-48 часов.",
      info: { email: "Email", emailValue: "support@ellagold.com", response: "Время ответа", responseValue: "24-48 часов", social: "Соцсети", location: "Местоположение", locationValue: "Нидерланды" },
      faq: { title: "Часто задаваемые вопросы", items: [] }
    },
    ar: {
      title: "اتصل بنا",
      subtitle: "نحب أن نسمع منك",
      form: { name: "اسمك", email: "البريد الإلكتروني", subject: "الموضوع", subjects: { general: "استفسار عام", support: "الدعم الفني", billing: "سؤال عن الفواتير", refund: "طلب استرداد", business: "استفسار تجاري" }, message: "رسالتك", messagePlaceholder: "كيف يمكننا مساعدتك؟", send: "إرسال", sending: "جاري الإرسال..." },
      success: "تم إرسال الرسالة! سنرد خلال 24-48 ساعة.",
      info: { email: "البريد الإلكتروني", emailValue: "support@ellagold.com", response: "وقت الرد", responseValue: "24-48 ساعة", social: "وسائل التواصل", location: "الموقع", locationValue: "هولندا" },
      faq: { title: "الأسئلة الشائعة", items: [] }
    }
  };

  const t = content[lang] || content.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.success);
      setForm({ name: '', email: '', subject: 'general', message: '' });
    } catch (error) {
      // Even if no backend endpoint, show success for UX
      toast.success(t.success);
      setForm({ name: '', email: '', subject: 'general', message: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="contact-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
            <Mail className="w-10 h-10 text-[#D4AF37]" />
            {t.title}
          </h1>
          <p className="text-gray-400 text-lg">{t.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="p-8 bg-white/5 rounded-2xl border border-white/10">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#D4AF37]" />
              {t.form.message}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label>{t.form.name} *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="John Doe"
                  className="mt-1 bg-white/5 border-white/10"
                  required
                  data-testid="contact-name"
                />
              </div>

              <div>
                <Label>{t.form.email} *</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  placeholder="you@example.com"
                  className="mt-1 bg-white/5 border-white/10"
                  required
                  data-testid="contact-email"
                />
              </div>

              <div>
                <Label>{t.form.subject}</Label>
                <select
                  value={form.subject}
                  onChange={(e) => setForm({...form, subject: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                  data-testid="contact-subject"
                >
                  {Object.entries(t.form.subjects).map(([key, value]) => (
                    <option key={key} value={key} className="bg-[#0A0A0A]">{value}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>{t.form.message} *</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({...form, message: e.target.value})}
                  placeholder={t.form.messagePlaceholder}
                  className="mt-1 bg-white/5 border-white/10 min-h-[150px]"
                  required
                  data-testid="contact-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                data-testid="contact-submit"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? t.form.sending : t.form.send}
              </Button>
            </form>
          </div>

          {/* Info & FAQ */}
          <div className="space-y-8">
            {/* Contact Info */}
            <div className="p-6 bg-white/5 rounded-xl border border-white/10">
              <h3 className="font-semibold mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-400">{t.info.email}</p>
                    <a href={`mailto:${t.info.emailValue}`} className="text-[#D4AF37] hover:underline">
                      {t.info.emailValue}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-400">{t.info.response}</p>
                    <p>{t.info.responseValue}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-400">{t.info.location}</p>
                    <p>{t.info.locationValue}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Instagram className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-400">{t.info.social}</p>
                    <a href="https://instagram.com/ellagoldofficial" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">
                      @ellagoldofficial
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            {t.faq.items && t.faq.items.length > 0 && (
              <div className="p-6 bg-white/5 rounded-xl border border-white/10">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <HelpCircle className="w-5 h-5 text-[#D4AF37]" />
                  {t.faq.title}
                </h3>
                <div className="space-y-4">
                  {t.faq.items.map((item, idx) => (
                    <div key={idx}>
                      <p className="font-medium text-[#D4AF37]">{item.q}</p>
                      <p className="text-sm text-gray-400 mt-1">{item.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
